/**
 * Компонент управления типами квалификаторов (QualType)
 * 
 * Функциональность:
 * - Отображение списка типов квалификаторов
 * - Создание нового типа квалификатора
 * - Редактирование существующего типа
 * - Удаление типа квалификатора (soft delete)
 * - Поиск и фильтрация
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PermissionGuard } from "@/components/PermissionGuard";
import { qualTypesApi } from "@/services/kls-api.service";
import type { QualType, QualTypeCreate, QualTypeUpdate } from "@/types/api-v2";
import { qualTypeSchema, type QualTypeFormData } from "@/lib/validations/kls";
import { Plus, Pencil, Trash2, Search, X, Upload, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import ImportDialog from "./ImportDialog";
import ExportDialog from "./ExportDialog";
import { ReferencesSectionHeader } from "@/components/admin/ReferencesSectionHeader";

export default function QualTypeManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<QualType | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortField, setSortField] = useState<"created_at" | "updated_at">("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    item: QualType | null;
  }>({ isOpen: false, item: null });
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // Форма с валидацией
  const form = useForm<QualTypeFormData>({
    resolver: zodResolver(qualTypeSchema),
    defaultValues: {
      qual_type_namef: "",
      qual_type_names: "",
      qual_type_code: "",
      qual_type_vers: 1,
    },
  });

  // Загрузка списка типов квалификаторов
  const { data: qualTypes = [], isLoading } = useQuery<QualType[]>({
    queryKey: ["qual-types", showDeleted],
    queryFn: () => qualTypesApi.list({ include_deleted: showDeleted }),
  });

  // Мутация создания
  const createMutation = useMutation({
    mutationFn: (data: QualTypeCreate) => qualTypesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qual-types"] });
      toast({
        title: "Успешно",
        description: "Тип квалификатора создан",
      });
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось создать тип квалификатора",
        variant: "destructive",
      });
    },
  });

  // Мутация обновления
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: QualTypeUpdate }) =>
      qualTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qual-types"] });
      toast({
        title: "Успешно",
        description: "Тип квалификатора обновлен",
      });
      setIsDialogOpen(false);
      setEditingItem(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось обновить тип квалификатора",
        variant: "destructive",
      });
    },
  });

  // Мутация удаления
  const deleteMutation = useMutation({
    mutationFn: (id: number) => qualTypesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qual-types"] });
      toast({
        title: "Успешно",
        description: "Тип квалификатора удален",
      });
      setDeleteConfirm({ isOpen: false, item: null });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось удалить тип квалификатора",
        variant: "destructive",
      });
    },
  });

  // Открытие диалога создания/редактирования
  const openDialog = (item?: QualType) => {
    if (item) {
      setEditingItem(item);
      form.reset({
        qual_type_namef: item.qual_type_namef,
        qual_type_names: item.qual_type_names || "",
        qual_type_code: item.qual_type_code || "",
        qual_type_vers: item.qual_type_vers,
      });
    } else {
      setEditingItem(null);
      form.reset({
        qual_type_namef: "",
        qual_type_names: "",
        qual_type_code: "",
        qual_type_vers: 1,
      });
    }
    setIsDialogOpen(true);
  };

  // Обработка отправки формы
  const onSubmit = (data: QualTypeFormData) => {
    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.qual_type_id,
        data: {
          qual_type_namef: data.qual_type_namef,
          qual_type_names: data.qual_type_names || undefined,
          qual_type_code: data.qual_type_code || undefined,
          qual_type_vers: data.qual_type_vers,
        },
      });
    } else {
      createMutation.mutate({
        qual_type_namef: data.qual_type_namef,
        qual_type_names: data.qual_type_names || undefined,
        qual_type_code: data.qual_type_code || undefined,
        qual_type_vers: data.qual_type_vers,
      });
    }
  };

  // Фильтрация и сортировка
  const filteredQualTypes = useMemo(() => {
    let filtered = qualTypes.filter((item) => {
      if (item.qual_type_is_del && !showDeleted) return false;
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.qual_type_namef.toLowerCase().includes(query) ||
        item.qual_type_names?.toLowerCase().includes(query) ||
        item.qual_type_code?.toLowerCase().includes(query)
      );
    });

    // Сортировка по датам
    filtered = [...filtered].sort((a, b) => {
      const aDate = sortField === "created_at" ? a.created_at : a.updated_at || a.created_at;
      const bDate = sortField === "created_at" ? b.created_at : b.updated_at || b.created_at;
      if (!aDate || !bDate) return 0;
      const aTime = new Date(aDate).getTime();
      const bTime = new Date(bDate).getTime();
      return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
    });

    return filtered;
  }, [qualTypes, searchQuery, showDeleted, sortField, sortDirection]);

  // Обработчик клика по заголовку сортировки
  const handleSortClick = (field: "created_at" | "updated_at") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  return (
    <div className="space-y-4">
      <ReferencesSectionHeader
        title="Типы квалификаторов"
        description="Группы квалификаторов KLS. Дальше на вкладке «Квалификаторы» создаются сами квалификаторы и привязка к типу."
        actions={
          <>
            <PermissionGuard permission="kls.export">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExportDialogOpen(true)}
              >
                <Download className="mr-2 h-4 w-4" />
                Экспорт
              </Button>
            </PermissionGuard>
            <PermissionGuard permission="kls.import">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsImportDialogOpen(true)}
              >
                <Upload className="mr-2 h-4 w-4" />
                Импорт
              </Button>
            </PermissionGuard>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleted(!showDeleted)}
            >
              {showDeleted ? "Скрыть удаленные" : "Показать удаленные"}
            </Button>
            <PermissionGuard permission="kls.qual_types.create">
              <Button onClick={() => openDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                Добавить тип
              </Button>
            </PermissionGuard>
          </>
        }
      />

      {/* Поиск */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Поиск по наименованию или коду..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
            onClick={() => setSearchQuery("")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Таблица */}
      <div className="border rounded-md overflow-hidden">
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Полное наименование</TableHead>
                <TableHead>Сокращенное наименование</TableHead>
                <TableHead>Код</TableHead>
                <TableHead>Версия</TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium hover:text-foreground"
                    onClick={() => handleSortClick("created_at")}
                  >
                    Создан
                    {sortField === "created_at" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium hover:text-foreground"
                    onClick={() => handleSortClick("updated_at")}
                  >
                    Обновлён
                    {sortField === "updated_at" && (
                      <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                    )}
                  </button>
                </TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-6">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : filteredQualTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-6">
                    {searchQuery ? "Ничего не найдено" : "Типы квалификаторов отсутствуют"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredQualTypes.map((item) => (
                  <TableRow key={item.qual_type_id}>
                    <TableCell>{item.qual_type_id}</TableCell>
                    <TableCell className="font-medium">{item.qual_type_namef}</TableCell>
                    <TableCell>{item.qual_type_names || "-"}</TableCell>
                    <TableCell>{item.qual_type_code || "-"}</TableCell>
                    <TableCell>{item.qual_type_vers}</TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={item.created_at ? new Date(item.created_at).toLocaleString("ru-RU") : "Нет данных"}
                    >
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString("ru-RU")
                        : "—"}
                    </TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={
                        item.updated_at
                          ? new Date(item.updated_at).toLocaleString("ru-RU")
                          : "Нет обновлений"
                      }
                    >
                      {item.updated_at
                        ? new Date(item.updated_at).toLocaleDateString("ru-RU")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {item.qual_type_is_del ? (
                        <Badge variant="destructive">Удален</Badge>
                      ) : (
                        <Badge variant="default">Активен</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <PermissionGuard permission="kls.qual_types.edit">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog(item)}
                            disabled={item.qual_type_is_del}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="kls.qual_types.delete">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm({ isOpen: true, item })}
                            disabled={item.qual_type_is_del}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Диалог создания/редактирования */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Редактировать тип квалификатора" : "Создать тип квалификатора"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="qual_type_namef"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Полное наименование *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Общероссийские классификаторы" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="qual_type_names"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Сокращенное наименование</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="ОК" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="qual_type_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Код типа классификатора</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="OK" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="qual_type_vers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Версия</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                        min={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    form.reset();
                    setEditingItem(null);
                  }}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingItem ? "Сохранить" : "Создать"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, item: null })}
        onConfirm={() => {
          if (deleteConfirm.item) {
            deleteMutation.mutate(deleteConfirm.item.qual_type_id);
          }
        }}
        title="Удалить тип квалификатора?"
        description={`Вы уверены, что хотите удалить тип квалификатора "${deleteConfirm.item?.qual_type_namef}"?`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="destructive"
      />
      
      {/* Диалоги импорта/экспорта */}
      <ImportDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        entityType="qual_type"
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["qual-types"] });
        }}
      />
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        entityType="qual_type"
      />
    </div>
  );
}

